import type { ApiClient } from '@saay/api-client';
import { AuthProvider } from '@saay/auth-web';
import { UiProvider } from '@saay/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';

import { PreferenceProvider, type WebAppId } from './preferences/index.js';
import type { AppRuntime } from './runtime.js';

const ApiClientContext = createContext<ApiClient | null>(null);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error('useApiClient must be used inside <AppProviders>.');
  }
  return client;
}

export function AppProviders({
  appId,
  children,
  runtime,
}: {
  readonly appId: WebAppId;
  readonly children: ReactNode;
  readonly runtime: AppRuntime;
}) {
  return (
    <UiProvider>
      <AuthProvider controller={runtime.auth}>
        <PreferenceProvider appId={appId}>
          <QueryClientProvider client={runtime.queryClient}>
            <ApiClientContext.Provider value={runtime.apiClient}>
              {children}
            </ApiClientContext.Provider>
          </QueryClientProvider>
        </PreferenceProvider>
      </AuthProvider>
    </UiProvider>
  );
}
